import { useState, useEffect } from "react";
import {
    Typography,
    Button,
    Card,
    CardContent,
    CardHeader,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
} from "@mui/material";
import { FaClock } from "react-icons/fa";
import { getAllWorkTypes } from "../../../Services/workTypeService";
import { getAllCompensationTypes } from "../../../Services/compensationService";
import { getAllUser, getAllUserByCreatedBy } from "../../../Services/authService";
import { getProjectByUserId } from "../../../Services/projectService";
import { getApprovalFormByProjectId } from "../../../Services/approvalFormService";
import {
    createTimeEntry,
    updateTimeEntry,
    getUserTimeEntries,
    approveTimeEntries,
    deleteTimeEntry, // Import the delete service
} from "../../../Services/timeEntryService";
import { toast } from "react-toastify";
import MainLayout from "../../components/layout/MainLayout";
import TimeEntryFormDialog from "../../../components/adminTimeLogManagement/TimeEntryFormDialog";
import { formatDate, getWeekDates } from "../../../components/adminTimeLogManagement/TimeListHelpers";
import TimeEntryFilters from "../../../components/adminTimeLogManagement/TimeEntryFilters";
import ActionButtons from "./components/ActionButtons";
import TimeEntriesTable from "./components/TimeEntriesTable";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default function TimeListAdminIndex() {

    const userId = useSelector(state => state?.auth?.loggedInUser._id)
    const [currentWeek, setCurrentWeek] = useState(getWeekDates(new Date()));
    const [timeEntries, setTimeEntries] = useState([]);
    const [filteredEntries, setFilteredEntries] = useState([]);
    const [projects, setProjects] = useState([]);
    const [scaffoldIds, setScaffoldIds] = useState([]);
    const [workTypes, setWorkTypes] = useState([]);
    const [compensationTypes, setCompensationTypes] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedEntries, setSelectedEntries] = useState([]);
    const [editingEntry, setEditingEntry] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [inlineEditing, setInlineEditing] = useState(null);
    const [error, setError] = useState(false);
    
    // Delete confirmation dialog states
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [entryToDelete, setEntryToDelete] = useState(null);
    const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
    
    // Add sort configuration state
    const [sortConfig, setSortConfig] = useState({
        field: 'date',
        direction: 'desc'
    });

    const [loading, setLoading] = useState({
        users: false,
        projects: false,
        scaffolds: false,
        workTypes: false,
        compensationTypes: false,
        timeEntries: false,
        saveEntry: false,
        approveEntries: false,
        deleteEntry: false,
        bulkDelete: false,
    });

    const [filters, setFilters] = useState({
        name: "",
        projectId: "",
        approvalStatus: "",
        searchTerm: "",
        startDate: null,
        endDate: null,
    });

    const getAllUsersData = async () => {
        try {
            setLoading((prev) => ({ ...prev, users: true }));
            const dataResponse = await getAllUserByCreatedBy(userId);
            const userDataResponse = dataResponse?.data;
            const filteredUsers = userDataResponse;

            setUsers(filteredUsers || []);
        } catch (error) {
            console.error("Error fetching subscription data:", error);
            toast.error(error.response?.data?.message || "Failed to load user data");
            setError("Failed to load user data. Please try again later.");
        } finally {
            setLoading((prev) => ({ ...prev, users: false }));
        }
    };

    const getAllProjects = async (userId) => {
        try {
            if (!userId) return;
            setLoading((prev) => ({ ...prev, projects: true }));
            const responseData = await getProjectByUserId(userId);
            if (responseData?.status) {
                setProjects(responseData?.data);
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
            toast.error("Failed to load projects", "error");
        } finally {
            setLoading((prev) => ({ ...prev, projects: false }));
        }
    };

    const getAllScaffoldIds = async (projectId) => {
        try {
            if (!projectId) return;
            setLoading((prev) => ({ ...prev, scaffolds: true }));
            const responseData = await getApprovalFormByProjectId(projectId);
            if (responseData?.success) {
                setScaffoldIds(responseData.data);
            }
        } catch (error) {
            console.error("Error fetching scaffold IDs:", error);
            toast.error("Failed to load scaffold IDs", "error");
        } finally {
            setLoading((prev) => ({ ...prev, scaffolds: false }));
        }
    };

    const getAllWorkTypesData = async () => {
        try {
            setLoading((prev) => ({ ...prev, workTypes: true }));
            const responseData = await getAllWorkTypes();
            if (responseData?.data) {
                setWorkTypes(responseData.data);
            }
        } catch (error) {
            console.error("Error fetching work types:", error);
            toast.error("Failed to load work types", "error");
        } finally {
            setLoading((prev) => ({ ...prev, workTypes: false }));
        }
    };

    const getAllCompensationTypesData = async () => {
        try {
            setLoading((prev) => ({ ...prev, compensationTypes: true }));
            const responseData = await getAllCompensationTypes();
            if (responseData?.data) {
                setCompensationTypes(responseData.data);
            }
        } catch (error) {
            console.error("Error fetching compensation types:", error);
            toast.error("Failed to load compensation types", "error");
        } finally {
            setLoading((prev) => ({ ...prev, compensationTypes: false }));
        }
    };

    const fetchAllTimeEntries = async () => {
        try {
            setLoading((prev) => ({ ...prev, timeEntries: true }));
            const response = await getUserTimeEntries();
            if (response?.success) {
                setTimeEntries(response.data);
                setFilteredEntries(response.data);
            }
        } catch (error) {
            console.error("Error fetching time entries:", error);
            toast.error("Failed to load time entries", "error");
        } finally {
            setLoading((prev) => ({ ...prev, timeEntries: false }));
        }
    };

    useEffect(() => {
        if (userId) {
            getAllUsersData();
            getAllProjects(userId)
            getAllWorkTypesData();
            getAllCompensationTypesData();
            fetchAllTimeEntries();
        }
    }, [userId]);

    // Helper function to get nested property value safely
    const getNestedValue = (obj, path) => {
        if (!obj) return null;

        const keys = path.split('.');
        return keys.reduce((acc, key) => {
            return acc && acc[key] !== undefined ? acc[key] : null;
        }, obj);
    };

    // Sort function for entries based on current sort configuration
    const sortEntries = (entries) => {
        if (!sortConfig.field) return entries;

        return [...entries].sort((a, b) => {
            // Get values based on the sort field (handling nested properties)
            let aValue = getNestedValue(a, sortConfig.field);
            let bValue = getNestedValue(b, sortConfig.field);

            // Handle special case for date field
            if (sortConfig.field === 'date') {
                aValue = new Date(a.date).getTime();
                bValue = new Date(b.date).getTime();
            }

            // Handle special case for totalHours (convert to number)
            if (sortConfig.field === 'totalHours') {
                aValue = parseFloat(aValue) || 0;
                bValue = parseFloat(bValue) || 0;
            }

            // Handle null or undefined values
            if (aValue === null || aValue === undefined) return sortConfig.direction === 'asc' ? -1 : 1;
            if (bValue === null || bValue === undefined) return sortConfig.direction === 'asc' ? 1 : -1;

            // String comparison for strings
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                const compareResult = aValue.localeCompare(bValue);
                return sortConfig.direction === 'asc' ? compareResult : -compareResult;
            }

            // Numeric comparison for numbers
            const compareResult = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            return sortConfig.direction === 'asc' ? compareResult : -compareResult;
        });
    };

    // Handle sort change
    const handleSortChange = (newSortConfig) => {
        setSortConfig(newSortConfig);
    };

    useEffect(() => {
        let result = timeEntries;

        // Apply search term filter
        if (filters.searchTerm && filters.searchTerm.trim() !== "") {
            const searchTermLower = filters.searchTerm.toLowerCase().trim();
            result = result.filter((entry) => {
                return (
                    (entry?.userId?.name &&
                        entry.userId.name.toLowerCase().includes(searchTermLower)) ||
                    (entry?.projectId?.projectName &&
                        entry.projectId.projectName
                            .toLowerCase()
                            .includes(searchTermLower)) ||
                    (entry?.scaffoldId?.scaffoldId &&
                        entry.scaffoldId.scaffoldId
                            .toLowerCase()
                            .includes(searchTermLower)) ||
                    (entry?.workTypeId?.name &&
                        entry.workTypeId.name.toLowerCase().includes(searchTermLower)) ||
                    (entry?.compensationTypeId?.name &&
                        entry.compensationTypeId.name
                            .toLowerCase()
                            .includes(searchTermLower)) ||
                    (entry?.comments &&
                        entry.comments.toLowerCase().includes(searchTermLower)) ||
                    (entry?.date &&
                        formatDate(new Date(entry.date))
                            .toLowerCase()
                            .includes(searchTermLower))
                );
            });
        }

        // Apply user filter
        if (filters.name && filters.name !== "all_users") {
            result = result.filter((entry) => entry?.userId?.name === filters.name);
        }

        // Apply project filter
        if (filters.projectId && filters.projectId !== "all_projects") {
            result = result.filter((entry) => {
                const entryProjectId =
                    typeof entry.projectId === "object"
                        ? entry.projectId._id
                        : entry.projectId;
                return entryProjectId === filters.projectId;
            });
        }

        // Apply approval status filter
        if (filters.approvalStatus && filters.approvalStatus !== "all_statuses") {
            result = result.filter(
                (entry) => entry.approvalStatus === filters.approvalStatus
            );
        }

        // Apply date range filter
        if (filters.startDate) {
            const startDateTimestamp = new Date(filters.startDate).setHours(0, 0, 0, 0);
            result = result.filter((entry) => {
                const entryDate = new Date(entry.date).setHours(0, 0, 0, 0);
                return entryDate >= startDateTimestamp;
            });
        }

        if (filters.endDate) {
            const endDateTimestamp = new Date(filters.endDate).setHours(23, 59, 59, 999);
            result = result.filter((entry) => {
                const entryDate = new Date(entry.date).setHours(0, 0, 0, 0);
                return entryDate <= endDateTimestamp;
            });
        }

        // Apply sorting
        result = sortEntries(result);

        setFilteredEntries(result);
    }, [filters, timeEntries, sortConfig]);

    const navigateWeek = (direction) => {
        const firstDay = new Date(currentWeek[0]);
        firstDay.setDate(firstDay.getDate() + (direction === "next" ? 7 : -7));
        setCurrentWeek(getWeekDates(firstDay));
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedEntries(filteredEntries?.map((entry) => entry._id));
        } else {
            setSelectedEntries([]);
        }
    };

    const handleSelectEntry = (entryId, checked) => {
        if (checked) {
            setSelectedEntries([...selectedEntries, entryId]);
        } else {
            setSelectedEntries(selectedEntries.filter((id) => id !== entryId));
        }
    };

    const approveSelected = async () => {
        if (selectedEntries.length === 0) return;

        try {
            setLoading((prev) => ({ ...prev, approveEntries: true }));
            const response = await approveTimeEntries(selectedEntries);

            if (response?.success) {
                fetchAllTimeEntries();
                setSelectedEntries([]);
                toast.success(
                    `Successfully approved ${selectedEntries.length} entries`
                );
            } else {
                toast.error(
                    `Failed to approve entries: ${response?.message || "Unknown error"}`
                );
            }
        } catch (error) {
            console.error("Error approving entries:", error);
            toast.error("Failed to approve some entries");
        } finally {
            setLoading((prev) => ({ ...prev, approveEntries: false }));
        }
    };

    // Delete single entry function
    const handleDeleteEntry = (entry) => {
        if (entry.approvalStatus === "approved") {
            toast.warning("Cannot delete approved entries");
            return;
        }
        setEntryToDelete(entry);
        setDeleteConfirmOpen(true);
    };

    // Confirm single delete
    const confirmDeleteEntry = async () => {
        if (!entryToDelete) return;

        try {
            setLoading((prev) => ({ ...prev, deleteEntry: true }));
            const response = await deleteTimeEntry(entryToDelete._id);

            if (response?.success || response?.status === 200 || !response?.error) {
                // Remove the deleted entry from state
                setTimeEntries(prevEntries => 
                    prevEntries.filter(entry => entry._id !== entryToDelete._id)
                );
                setFilteredEntries(prevEntries => 
                    prevEntries.filter(entry => entry._id !== entryToDelete._id)
                );
                
                toast.success("Time entry deleted successfully");
            } else {
                toast.error(response?.message || "Failed to delete entry");
            }
        } catch (error) {
            console.error("Error deleting entry:", error);
            toast.error("Failed to delete entry");
        } finally {
            setLoading((prev) => ({ ...prev, deleteEntry: false }));
            setDeleteConfirmOpen(false);
            setEntryToDelete(null);
        }
    };

    // Bulk delete function
    const handleBulkDelete = () => {
        if (selectedEntries.length === 0) {
            toast.warning("Please select entries to delete");
            return;
        }

        // Check if any selected entries are approved
        const selectedEntryObjects = filteredEntries.filter(entry => 
            selectedEntries.includes(entry._id)
        );
        const approvedEntries = selectedEntryObjects.filter(entry => 
            entry.approvalStatus === "approved"
        );

        if (approvedEntries.length > 0) {
            toast.warning("Cannot delete approved entries. Please unselect approved entries first.");
            return;
        }

        setBulkDeleteConfirmOpen(true);
    };

    // Confirm bulk delete
    const confirmBulkDelete = async () => {
        try {
            setLoading((prev) => ({ ...prev, bulkDelete: true }));
            const deletePromises = selectedEntries.map(entryId => 
                deleteTimeEntry(entryId)
            );

            const results = await Promise.allSettled(deletePromises);
            
            let successCount = 0;
            let failCount = 0;

            results.forEach((result, index) => {
                if (result.status === 'fulfilled' && 
                    (result.value?.success || result.value?.status === 200 || !result.value?.error)) {
                    successCount++;
                } else {
                    failCount++;
                }
            });

            if (successCount > 0) {
                // Remove successfully deleted entries from state
                setTimeEntries(prevEntries => 
                    prevEntries.filter(entry => !selectedEntries.includes(entry._id))
                );
                setFilteredEntries(prevEntries => 
                    prevEntries.filter(entry => !selectedEntries.includes(entry._id))
                );
                
                toast.success(`Successfully deleted ${successCount} entries`);
            }

            if (failCount > 0) {
                toast.error(`Failed to delete ${failCount} entries`);
            }

            setSelectedEntries([]);
        } catch (error) {
            console.error("Error during bulk delete:", error);
            toast.error("Failed to delete entries");
        } finally {
            setLoading((prev) => ({ ...prev, bulkDelete: false }));
            setBulkDeleteConfirmOpen(false);
        }
    };

    const exportData = (format) => {
        toast.success(`Exporting data as ${format.toUpperCase()}`);
    };

    // Handle PDF export
    const handleExportPDF = () => {
        try {
            // Create new PDF document
            const doc = new jsPDF('landscape');
            const companyName = "Your Company Name"; // Replace with actual company name
            const currentDate = new Date().toLocaleDateString();

            // Set document properties
            doc.setProperties({
                title: "Time Entries Report",
                subject: "Time entries report",
                author: companyName,
                creator: companyName
            });

            // Add document title and header
            doc.setFontSize(18);
            doc.setTextColor(40, 40, 40);
            doc.text("Time Entries Report", 150, 15, { align: "center" });

            // Add company logo placeholder (could be replaced with an actual logo)
            doc.setDrawColor(0);
            doc.setFillColor(44, 62, 80);
            doc.rect(14, 10, 30, 10, "F");
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.text(companyName, 29, 16, { align: "center" });

            // Reset text color
            doc.setTextColor(40, 40, 40);

            // Add report metadata
            doc.setFontSize(10);
            doc.text(`Generated on: ${currentDate}`, 14, 30);

            // Add filter information if filters are applied
            let filterText = "Filters applied: ";
            let hasFilters = false;

            if (filters.name && filters.name !== "all_users") {
                filterText += `User: ${filters.name}, `;
                hasFilters = true;
            }

            if (filters.projectId && filters.projectId !== "all_projects") {
                const project = projects.find(p => p._id === filters.projectId);
                filterText += `Project: ${project?.projectName || 'Unknown'}, `;
                hasFilters = true;
            }

            if (filters.approvalStatus && filters.approvalStatus !== "all_statuses") {
                filterText += `Status: ${filters.approvalStatus}, `;
                hasFilters = true;
            }

            if (filters.startDate) {
                filterText += `From: ${new Date(filters.startDate).toLocaleDateString()}, `;
                hasFilters = true;
            }

            if (filters.endDate) {
                filterText += `To: ${new Date(filters.endDate).toLocaleDateString()}, `;
                hasFilters = true;
            }

            // Remove trailing comma and space if there are filters
            if (hasFilters) {
                filterText = filterText.slice(0, -2);
                doc.text(filterText, 14, 35);
            } else {
                doc.text("No filters applied", 14, 35);
            }

            // Calculate total hours
            const totalHrs = calculateTotalHours();
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text(`Total Hours: ${totalHrs}`, 270, 35, { align: "right" });
            doc.setFont(undefined, 'normal');

            // Prepare table data
            const tableColumn = [
                "User",
                "Date",
                "Project",
                "Scaffold ID",
                "Hours",
                "Work Type",
                "Add. Compensation",
                "Status"
            ];

            const tableRows = [];

            filteredEntries.forEach(entry => {
                const userData = [
                    entry?.userId?.name || "-",
                    formatDate(new Date(entry.date)),
                    entry?.projectId?.projectName || "-",
                    entry?.scaffoldId?.scaffoldId || "-",
                    entry.totalHours || "0",
                    entry?.workTypeId?.name || "-",
                    entry?.compensationTypeId?.name || "-",
                    entry.approvalStatus === "approved" ? "Approved" : "Pending"
                ];
                tableRows.push(userData);
            });

            // Define custom styles for the table
            const tableStyles = {
                startY: 45,
                headStyles: {
                    fillColor: [44, 62, 80],
                    textColor: 255,
                    fontStyle: 'bold'
                },
                alternateRowStyles: {
                    fillColor: [240, 240, 240]
                },
                columnStyles: {
                    0: { cellWidth: 35 }, // User
                    1: { cellWidth: 25 }, // Date
                    2: { cellWidth: 40 }, // Project
                    3: { cellWidth: 30 }, // Scaffold ID
                    4: { cellWidth: 15 }, // Hours
                    5: { cellWidth: 30 }, // Work Type
                    6: { cellWidth: 35 }, // Additional Compensation
                    7: { cellWidth: 20 }  // Status
                },
                margin: { top: 40 },
                body: tableRows
            };

            // Add table to document
            doc.autoTable({
                head: [tableColumn],
                body: tableRows,
                startY: 45,
                theme: 'grid',
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                    lineColor: [44, 62, 80],
                    lineWidth: 0.1
                },
                headStyles: tableStyles.headStyles,
                alternateRowStyles: tableStyles.alternateRowStyles,
                columnStyles: tableStyles.columnStyles,
                margin: tableStyles.margin,
                didDrawPage: function (data) {
                    // Add page number at the bottom
                    const pageCount = doc.internal.getNumberOfPages();
                    const currentPage = data.pageNumber;
                    doc.setFontSize(8);
                    doc.text(`Page ${currentPage} of ${pageCount}`, 14, doc.internal.pageSize.height - 10);
                    doc.text(`Generated on: ${currentDate}`, doc.internal.pageSize.width - 14, doc.internal.pageSize.height - 10, { align: "right" });
                }
            });

            // Add summary section
            const lastPage = doc.internal.getNumberOfPages();
            doc.setPage(lastPage);
            const finalY = (doc.lastAutoTable?.finalY || 45) + 15;

            if (finalY < doc.internal.pageSize.height - 40) {
                doc.setFontSize(12);
                doc.setFont(undefined, 'bold');
                doc.text("Summary", 14, finalY);
                doc.setFont(undefined, 'normal');
                doc.setFontSize(10);

                doc.text(`Total Entries: ${filteredEntries.length}`, 14, finalY + 10);
                doc.text(`Total Hours: ${totalHrs}`, 14, finalY + 20);

                // Count the number of approved and pending entries
                const approvedCount = filteredEntries.filter(entry => entry.approvalStatus === "approved").length;
                const pendingCount = filteredEntries.length - approvedCount;

                doc.text(`Approved Entries: ${approvedCount}`, 14, finalY + 30);
                doc.text(`Pending Entries: ${pendingCount}`, 14, finalY + 40);
            }

            // Save the PDF
            doc.save(`TimeEntries_Report_${currentDate.replace(/\//g, '-')}.pdf`);

            toast.success("PDF Report generated successfully!");
        } catch (error) {
            console.error("Error generating PDF:", error);
            toast.error("Failed to generate PDF report");
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!editingEntry.userId) {
            errors.userId = "User is required";
        }

        if (!editingEntry.projectId) {
            errors.projectId = "Project is required";
        }

        if (!editingEntry.scaffoldId) {
            errors.scaffoldId = "Scaffold ID is required";
        }

        if (!editingEntry.workTypeId) {
            errors.workTypeId = "Work type is required";
        }

        if (!editingEntry.date) {
            errors.date = "Date is required";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const saveEditedEntry = async () => {
        if (!editingEntry) return;

        if (!validateForm()) {
            toast.error("Please fill in all required fields", "error");
            return;
        }

        const updatedEntry = {
            ...editingEntry,
        };

        try {
            setLoading((prev) => ({ ...prev, saveEntry: true }));

            if (updatedEntry.id?.startsWith("new")) {
                delete updatedEntry.id;
                const response = await createTimeEntry(updatedEntry);
                if (response.success) {
                    const project = projects.find(
                        (p) => p._id === updatedEntry.projectId
                    );
                    const scaffold = scaffoldIds.find(
                        (s) => s._id === updatedEntry.scaffoldId
                    );
                    const workType = workTypes.find(
                        (w) => w._id === updatedEntry.workTypeId
                    );
                    const user = users?.find((u) => u._id === updatedEntry.userId);

                    const completedEntry = {
                        ...response.data,
                        projectId: {
                            _id: updatedEntry.projectId,
                            projectName: project?.projectName || "Unknown",
                        },
                        scaffoldId: {
                            _id: updatedEntry.scaffoldId,
                            scaffoldId: scaffold?.scaffolderowner || "Unknown",
                        },
                        workTypeId: {
                            _id: updatedEntry.workTypeId,
                            name: workType?.name || "Unknown",
                        },
                        userId: { _id: updatedEntry.userId, name: user?.name || "Unknown" },
                    };

                    setTimeEntries([...timeEntries, completedEntry]);
                    toast.success("Time entry created successfully");
                } else {
                    toast.error(response.message || "Failed to create entry", "error");
                }
            } else {
                const response = await updateTimeEntry(updatedEntry._id, updatedEntry);
                if (response.success) {
                    const updatedEntries = timeEntries?.map((entry) => {
                        if (entry._id === updatedEntry._id) {
                            return {
                                ...updatedEntry,
                                projectId:
                                    typeof updatedEntry.projectId === "object"
                                        ? updatedEntry.projectId
                                        : {
                                            _id: updatedEntry.projectId,
                                            projectName:
                                                projects.find((p) => p._id === updatedEntry.projectId)
                                                    ?.projectName || "Unknown",
                                        },
                                scaffoldId:
                                    typeof updatedEntry.scaffoldId === "object"
                                        ? updatedEntry.scaffoldId
                                        : {
                                            _id: updatedEntry.scaffoldId,
                                            scaffoldId:
                                                scaffoldIds.find(
                                                    (s) => s._id === updatedEntry.scaffoldId
                                                )?.scaffolderowner || "Unknown",
                                        },
                                workTypeId:
                                    typeof updatedEntry.workTypeId === "object"
                                        ? updatedEntry.workTypeId
                                        : {
                                            _id: updatedEntry.workTypeId,
                                            name:
                                                workTypes.find(
                                                    (w) => w._id === updatedEntry.workTypeId
                                                )?.name || "Unknown",
                                        },
                                userId:
                                    typeof updatedEntry.userId === "object"
                                        ? updatedEntry.userId
                                        : {
                                            _id: updatedEntry.userId,
                                            name:
                                                users?.find((u) => u._id === updatedEntry.userId)
                                                    ?.name || "Unknown",
                                        },
                            };
                        }
                        return entry;
                    });
                    setTimeEntries(updatedEntries);
                    toast.success("Time entry updated successfully");
                } else {
                    toast.error(response.message || "Failed to update entry", "error");
                }
            }
            setEditingEntry(null);
        } catch (error) {
            console.error("Error saving entry:", error);
            toast.error("Failed to save entry", "error");
        } finally {
            setLoading((prev) => ({ ...prev, saveEntry: false }));
        }
    };

    const addNewEntry = () => {
        const newEntry = {
            id: `new-${Date.now()}`,
            name: "",
            userId: "",
            projectId: "",
            projectName: "",
            scaffoldId: "",
            scaffoldName: "",
            date: new Date().toISOString().split("T")[0],
            workTypeId: "",
            workTypeName: "",
            compensationTypeId: null,
            compensationTypeName: null,
            comments: "",
            approvalStatus: "pending",
        };

        setEditingEntry(newEntry);
        setFormErrors({});
    };

    const handleUserChange = (userId, userName) => {
        if (Array.isArray(userId)) {
            setSelectedUsers(userId);
            if (userId.length > 0) {
                const firstUser = users?.find((u) => u._id === userId[0]);
                setEditingEntry({
                    ...editingEntry,
                    userId: userId[0],
                    name: firstUser?.name || "",
                    projectId: "",
                    projectName: "",
                    scaffoldId: "",
                    scaffoldName: "",
                });
                getAllProjects(userId[0]);
            }
        } else {
            setEditingEntry({
                ...editingEntry,
                userId: userId,
                name: userName,
                projectId: "",
                projectName: "",
                scaffoldId: "",
                scaffoldName: "",
            });
            setFormErrors({ ...formErrors, userId: null });
            getAllProjects(userId);
        }
    };

    const handleProjectChange = (projectId, projectName) => {
        setEditingEntry({
            ...editingEntry,
            projectId: projectId,
            projectName: projectName,
            scaffoldId: "",
            scaffoldName: "",
        });
        setFormErrors({ ...formErrors, projectId: null });

        getAllScaffoldIds(projectId);
    };

    const approveEntry = async (entryId) => {
        try {
            const entryIdsInArray = [entryId];
            const updatedEntries = await approveTimeEntries(entryIdsInArray);
            if (updatedEntries?.success) {
                fetchAllTimeEntries();
                toast.success("Entry approved successfully");
            }
        } catch (error) {
            console.error("Error approving entry:", error);
            toast.error("Failed to approve entry", "error");
        }
    };

    const startInlineEdit = (entry, field) => {
        if (entry.approvalStatus === "approved") {
            toast.warning("Cannot edit approved entries");
            return;
        }

        setInlineEditing({ entryId: entry._id, field });

        if (field === "scaffoldId" && entry.projectId) {
            const projectId =
                typeof entry.projectId === "object"
                    ? entry.projectId._id
                    : entry.projectId;
            getAllScaffoldIds(projectId);
        }

        if (field === "projectId" && entry.userId) {
            const userId =
                typeof entry.userId === "object" ? entry.userId._id : entry.userId;
            getAllProjects(userId);
        }
    };

    const handleInlineChange = (entry, field, value) => {
        const updatedEntries = timeEntries?.map((e) => {
            if (e._id === entry._id) {
                let updatedEntry = { ...e };

                if (field === "projectId") {
                    const project = projects.find((p) => p._id === value);
                    updatedEntry.projectId = {
                        _id: value,
                        projectName: project?.projectName || "Unknown",
                    };
                    updatedEntry.scaffoldId = { _id: "", scaffoldId: "" };
                } else if (field === "scaffoldId") {
                    const scaffold = scaffoldIds.find((s) => s._id === value);
                    updatedEntry.scaffoldId = {
                        _id: value,
                        scaffoldId: scaffold?.scaffolderowner || "Unknown",
                    };
                } else {
                    updatedEntry[field] = value;
                }

                return updatedEntry;
            }
            return e;
        });

        setTimeEntries(updatedEntries);
    };

    const saveInlineEdit = async (entry) => {
        try {
            const entryToUpdate = timeEntries.find((e) => e._id === entry._id);
            if (!entryToUpdate) return;

            const updateData = {
                ...entryToUpdate,
                projectId:
                    typeof entryToUpdate.projectId === "object"
                        ? entryToUpdate.projectId._id
                        : entryToUpdate.projectId,
                scaffoldId:
                    typeof entryToUpdate.scaffoldId === "object"
                        ? entryToUpdate.scaffoldId._id
                        : entryToUpdate.scaffoldId,
                workTypeId:
                    typeof entryToUpdate.workTypeId === "object"
                        ? entryToUpdate.workTypeId._id
                        : entryToUpdate.workTypeId,
                userId:
                    typeof entryToUpdate.userId === "object"
                        ? entryToUpdate.userId._id
                        : entryToUpdate.userId,
                compensationTypeId: entryToUpdate.compensationTypeId
                    ? typeof entryToUpdate.compensationTypeId === "object"
                        ? entryToUpdate.compensationTypeId._id
                        : entryToUpdate.compensationTypeId
                    : null,
            };

            const response = await updateTimeEntry(entry._id, updateData);

            if (response.success) {
                toast.success("Entry updated successfully");
            } else {
                toast.error(response.message || "Failed to update entry");
                fetchAllTimeEntries();
            }
        } catch (error) {
            console.error("Error saving inline edit:", error);
            toast.error("Failed to save changes");
            fetchAllTimeEntries();
        } finally {
            setInlineEditing(null);
        }
    };

    const cancelInlineEdit = () => {
        setInlineEditing(null);
        fetchAllTimeEntries();
    };

    const createMultipleEntries = async () => {
        if (!editingEntry || !selectedUsers.length) return;

        if (!validateForm()) {
            toast.error("Please fill in all required fields", "error");
            return;
        }

        try {
            setLoading((prev) => ({ ...prev, saveEntry: true }));
            const successEntries = [];
            const errors = [];
            await Promise.all(
                selectedUsers?.map(async (userId) => {
                    const user = users?.find((u) => u._id === userId);
                    const entryData = {
                        ...editingEntry,
                        userId: userId,
                        name: user?.name || "",
                    };

                    if (entryData.id?.startsWith("new")) {
                        delete entryData.id;
                    }

                    try {
                        const response = await createTimeEntry(entryData);
                        if (response.success) {
                            successEntries.push(response.data);
                        } else {
                            errors.push(
                                `Failed for ${user?.name}: ${response.message || "Unknown error"
                                }`
                            );
                        }
                    } catch (error) {
                        errors.push(
                            `Failed for ${user?.name}: ${error.message || "Unknown error"}`
                        );
                    }
                })
            );

            if (successEntries.length > 0) {
                const newEntries = successEntries?.map((entry) => {
                    const project = projects.find((p) => p._id === entry.projectId);
                    const scaffold = scaffoldIds.find((s) => s._id === entry.scaffoldId);
                    const workType = workTypes.find((w) => w._id === entry.workTypeId);
                    const user = users?.find((u) => u._id === entry.userId);

                    return {
                        ...entry,
                        projectId: {
                            _id: entry.projectId,
                            projectName: project?.projectName || "Unknown",
                        },
                        scaffoldId: {
                            _id: entry.scaffoldId,
                            scaffoldId: scaffold?.scaffolderowner || "Unknown",
                        },
                        workTypeId: {
                            _id: entry.workTypeId,
                            name: workType?.name || "Unknown",
                        },
                        userId: { _id: entry.userId, name: user?.name || "Unknown" },
                    };
                });

                setTimeEntries([...timeEntries, ...newEntries]);
                toast.success(`Created ${successEntries.length} entries successfully`);
            }

            if (errors.length > 0) {
                errors.forEach((error) => toast.error(error));
            }

            if (successEntries.length > 0) {
                setEditingEntry(null);
                setSelectedUsers([]);
            }
        } catch (error) {
            console.error("Error creating multiple entries:", error);
            toast.error("Failed to create entries", "error");
        } finally {
            setLoading((prev) => ({ ...prev, saveEntry: false }));
        }
    };

    const calculateTotalHours = () => {
        return filteredEntries
            .reduce((total, entry) => {
                const hours = parseFloat(entry.totalHours);
                return total + (isNaN(hours) ? 0 : hours);
            }, 0)
            .toFixed(2);
    };

    return (
        <MainLayout title={"Time Tracking List"}>
            <Card>
                <CardHeader
                    title={
                        <Typography
                            variant="h5"
                            style={{ display: "flex", alignItems: "center", gap: "8px" }}
                        >
                            <FaClock style={{ color: "#1976d2" }} />
                            Time Entries Management
                        </Typography>
                    }
                />
                <CardContent>
                    <TimeEntryFilters
                        filters={filters}
                        setFilters={setFilters}
                        users={users}
                        projects={projects}
                        loading={loading}
                        handleExportPDF={handleExportPDF}
                    />

                    <ActionButtons
                        selectedEntries={selectedEntries}
                        addNewEntry={addNewEntry}
                        approveSelected={approveSelected}
                        handleBulkDelete={handleBulkDelete}
                        exportData={exportData}
                        loading={loading}
                    />

                    <TimeEntriesTable
                        loading={loading.timeEntries}
                        filteredEntries={filteredEntries}
                        selectedEntries={selectedEntries}
                        handleSelectAll={handleSelectAll}
                        handleSelectEntry={handleSelectEntry}
                        approveEntry={approveEntry}
                        handleDeleteEntry={handleDeleteEntry}
                        setEditingEntry={setEditingEntry}
                        startInlineEdit={startInlineEdit}
                        handleInlineChange={handleInlineChange}
                        saveInlineEdit={saveInlineEdit}
                        cancelInlineEdit={cancelInlineEdit}
                        inlineEditing={inlineEditing}
                        projects={projects}
                        scaffoldIds={scaffoldIds}
                        searchTerm={filters.searchTerm}
                        totalHours={calculateTotalHours()}
                        sortConfig={sortConfig}
                        onSortChange={handleSortChange}
                    />
                    {filteredEntries.length > 0 && (
                        <Box sx={{ mb: 2, mt: 2 }}>
                            <Typography variant="body2">
                                {filteredEntries.length}{" "}
                                {filteredEntries.length === 1 ? "entry" : "entries"} found
                                {filters.searchTerm ? ` matching "${filters.searchTerm}"` : ""}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Single Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete this time entry for{" "}
                        <strong>{entryToDelete?.userId?.name}</strong> on{" "}
                        <strong>{entryToDelete ? formatDate(new Date(entryToDelete.date)) : ''}</strong>?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setDeleteConfirmOpen(false)}
                        disabled={loading.deleteEntry}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={confirmDeleteEntry}
                        color="error"
                        variant="contained"
                        disabled={loading.deleteEntry}
                    >
                        {loading.deleteEntry ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Delete Confirmation Dialog */}
            <Dialog
                open={bulkDeleteConfirmOpen}
                onClose={() => setBulkDeleteConfirmOpen(false)}
                aria-labelledby="bulk-delete-dialog-title"
                aria-describedby="bulk-delete-dialog-description"
            >
                <DialogTitle id="bulk-delete-dialog-title">
                    Confirm Bulk Delete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="bulk-delete-dialog-description">
                        Are you sure you want to delete <strong>{selectedEntries.length}</strong> selected time entries?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setBulkDeleteConfirmOpen(false)}
                        disabled={loading.bulkDelete}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={confirmBulkDelete}
                        color="error"
                        variant="contained"
                        disabled={loading.bulkDelete}
                    >
                        {loading.bulkDelete ? "Deleting..." : "Delete All"}
                    </Button>
                </DialogActions>
            </Dialog>

            {editingEntry && (
                <TimeEntryFormDialog
                    editingEntry={editingEntry}
                    setEditingEntry={setEditingEntry}
                    loading={loading}
                    users={users}
                    projects={projects}
                    scaffoldIds={scaffoldIds}
                    workTypes={workTypes}
                    compensationTypes={compensationTypes}
                    formErrors={formErrors}
                    selectedUsers={selectedUsers}
                    handleUserChange={handleUserChange}
                    handleProjectChange={handleProjectChange}
                    saveEditedEntry={saveEditedEntry}
                    createMultipleEntries={createMultipleEntries}
                />
            )}
        </MainLayout>
    );
}